import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, UploadCloud, FileText, X, Truck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { company: "", mc_number: "", dot_number: "", contact_name: "", email: "", phone: "", equipment: "", lanes: "", message: "" };
const MAX = 10 * 1024 * 1024;
const ALLOWED = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".xls", ".xlsx", ".zip"];

export default function CarrierPartnerModal({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const inputRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (f) => {
    if (!f) return;
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) { toast.error("Unsupported file type. Use PDF, DOC, image, XLS or ZIP."); return; }
    if (f.size > MAX) { toast.error("File too large — max 10MB."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.mc_number || !form.contact_name || !form.email) {
      toast.error("Please fill company, MC number, contact name and email.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await axios.post(`${API}/carrier-partner`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      setForm(empty); setFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(() => setSent(false), 250);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="carrier-partner-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
        {sent ? (
          <div data-testid="carrier-partner-success" className="text-center space-y-5 py-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-500">
              <Check className="w-7 h-7" />
            </div>
            <div>
              <h3 className="font-heading font-extrabold text-2xl tracking-tight">Application received!</h3>
              <p className="mt-2 text-muted-foreground text-sm max-w-sm mx-auto">
                Thanks for your interest in partnering with Conexa Relay. Our carrier team will review your details and reach out shortly.
              </p>
            </div>
            <button type="button" data-testid="carrier-partner-close" onClick={() => handleOpenChange(false)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-8 py-3 text-sm hover:shadow-[0_0_40px_hsl(var(--primary)/0.5)] transition-shadow">
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-extrabold text-2xl tracking-tight flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" /> Become a carrier partner
              </DialogTitle>
              <DialogDescription>
                Join our vetted carrier network. Share your details and upload your carrier package — we'll take it from there.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} data-testid="carrier-partner-form" className="space-y-4 mt-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="carrier-company" placeholder="Company / Carrier name *" value={form.company} onChange={(e) => set("company", e.target.value)} className="bg-background/50 h-12" />
                <Input data-testid="carrier-mc" placeholder="MC number *" value={form.mc_number} onChange={(e) => set("mc_number", e.target.value)} className="bg-background/50 h-12" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="carrier-dot" placeholder="DOT number" value={form.dot_number} onChange={(e) => set("dot_number", e.target.value)} className="bg-background/50 h-12" />
                <Input data-testid="carrier-contact" placeholder="Contact name *" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="bg-background/50 h-12" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="carrier-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-background/50 h-12" />
                <Input data-testid="carrier-phone" placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-background/50 h-12" />
              </div>
              <Input data-testid="carrier-equipment" placeholder="Equipment types (Dry Van, Reefer, Flatbed…)" value={form.equipment} onChange={(e) => set("equipment", e.target.value)} className="bg-background/50 h-12" />
              <Input data-testid="carrier-lanes" placeholder="Lanes / coverage area" value={form.lanes} onChange={(e) => set("lanes", e.target.value)} className="bg-background/50 h-12" />
              <Textarea data-testid="carrier-message" placeholder="Anything else we should know?" value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-background/50 min-h-[80px]" />

              <input ref={inputRef} type="file" data-testid="carrier-file-input" className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.zip"
                onChange={(e) => pickFile(e.target.files?.[0])} />
              {file ? (
                <div data-testid="carrier-file-selected" className="flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3">
                  <FileText className="w-5 h-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button type="button" data-testid="carrier-file-remove" onClick={() => setFile(null)}
                    className="text-muted-foreground hover:text-destructive transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button type="button" data-testid="carrier-file-btn" onClick={() => inputRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border hover:border-primary/50 hover:bg-secondary/40 transition-colors py-6 text-center">
                  <UploadCloud className="w-6 h-6 text-primary" />
                  <span className="text-sm font-medium">Upload carrier package / profile</span>
                  <span className="text-xs text-muted-foreground">PDF, DOC, image, XLS or ZIP · up to 10MB</span>
                </button>
              )}

              <button type="submit" disabled={loading} data-testid="carrier-partner-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-4 hover:shadow-[0_0_40px_hsl(var(--primary)/0.5)] transition-shadow disabled:opacity-60">
                {loading ? "Sending..." : "Submit Application"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
